import {
  AcademicCapIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  StarIcon,
  ChevronRightIcon,
  UserGroupIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline'

export default function KnowledgeTreeHelp() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center mb-6">
          <AcademicCapIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Knowledge Tree</h1>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            The Knowledge Tree is a comprehensive knowledge base that organizes workplace processes, tasks, and troubleshooting
            information in a hierarchical structure. Access detailed procedures, common issues, and helpful solutions for efficient problem-solving.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Key Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <AcademicCapIcon className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Hierarchical Structure</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Navigate through organized processes, tasks, substeps, issues, and help resources in a clear tree structure.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <MagnifyingGlassIcon className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Advanced Search</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Search across all content including process names, task descriptions, issues, and help information.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Issue Resolution</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Find solutions to common problems with detailed troubleshooting steps and expert guidance.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <StarIcon className="h-6 w-6 text-yellow-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Rated Solutions</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Access community-rated help solutions to quickly identify the most effective approaches.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Navigation Structure</h2>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Knowledge Tree Hierarchy</h3>

            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-600 rounded-full mr-3 flex-shrink-0"></div>
                <div>
                  <h4 className="font-medium text-blue-900">Process Level</h4>
                  <p className="text-blue-700 text-sm">Top-level business processes (e.g., "Customer Onboarding", "Quality Control")</p>
                </div>
              </div>

              <div className="ml-6 border-l-2 border-blue-200 pl-4">
                <div className="flex items-center">
                  <ChevronRightIcon className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800">Task Level</h4>
                    <p className="text-blue-700 text-sm">Specific tasks within each process (numbered steps)</p>
                  </div>
                </div>
              </div>

              <div className="ml-12 border-l-2 border-blue-200 pl-4">
                <div className="flex items-center">
                  <ChevronRightIcon className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800">Substep Level</h4>
                    <p className="text-blue-700 text-sm">Detailed procedures and instructions</p>
                  </div>
                </div>
              </div>

              <div className="ml-18 border-l-2 border-red-200 pl-4">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-800">Issues & Help</h4>
                    <p className="text-red-700 text-sm">Common problems and rated solutions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Search & Filtering</h2>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Process Filter</h3>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Process</label>
                  <select className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed">
                    <option>All Processes</option>
                    <option>Customer Onboarding</option>
                    <option>Quality Control</option>
                    <option>Inventory Management</option>
                    <option>Safety Procedures</option>
                  </select>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Search Functionality</h3>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search Content</label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full p-2 pl-8 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                      placeholder="Search substeps, issues, help..."
                    />
                    <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Example Knowledge Tree</h2>

          <div className="bg-gradient-to-br from-blue-50 via-white to-blue-50 rounded-lg p-6 mb-8 border border-blue-200">
            <div className="space-y-6">
              {/* Process Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4 rounded-lg">
                <h3 className="text-xl font-bold">Customer Onboarding Process</h3>
                <p className="text-blue-100 text-sm mt-1">Complete guide for new customer setup and integration</p>
              </div>

              {/* Task 1 */}
              <div className="ml-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Initial Customer Verification</h4>
                </div>

                {/* Substep 1.1 */}
                <div className="ml-8 mb-4">
                  <div className="flex items-center mb-2">
                    <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-bold text-xs">1.1</span>
                    </div>
                    <span className="text-gray-700">Collect and validate customer identification documents</span>
                  </div>
                </div>

                {/* Issue with Help */}
                <div className="ml-8">
                  <div className="border-l-4 border-red-400 pl-4 mb-4">
                    <div className="bg-red-50 p-3 rounded-lg">
                      <h5 className="font-medium text-red-800 mb-1">Issue: Document Upload Failed</h5>
                      <p className="text-red-700 text-sm mb-2">Customer unable to upload identification documents due to file size limits</p>

                      <div className="bg-white p-3 rounded border border-red-200">
                        <h6 className="font-medium text-green-800 mb-1">Help: File Size Troubleshooting</h6>
                        <p className="text-green-700 text-sm mb-2">Check file size limits and compression options</p>
                        <div className="flex text-yellow-500">
                          {[...Array(4)].map((_, i) => (
                            <StarIcon key={i} className="h-4 w-4" />
                          ))}
                          <StarIcon className="h-4 w-4 text-gray-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task 2 */}
              <div className="ml-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">System Account Creation</h4>
                </div>

                {/* Substep 2.1 */}
                <div className="ml-8 mb-4">
                  <div className="flex items-center mb-2">
                    <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-bold text-xs">2.1</span>
                    </div>
                    <span className="text-gray-700">Generate login credentials and access permissions</span>
                  </div>
                </div>

                {/* Substep 2.2 */}
                <div className="ml-8 mb-4">
                  <div className="flex items-center mb-2">
                    <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-bold text-xs">2.2</span>
                    </div>
                    <span className="text-gray-700">Send welcome email with login instructions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Use</h2>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Browse by Process</h4>
                <p className="text-gray-600 text-sm">Use the process dropdown to filter content by specific business areas.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Search Content</h4>
                <p className="text-gray-600 text-sm">Enter keywords to search across all processes, tasks, issues, and help information.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Navigate Hierarchy</h4>
                <p className="text-gray-600 text-sm">Click through processes → tasks → substeps to access detailed procedures.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Access Help Solutions</h4>
                <p className="text-gray-600 text-sm">Review rated help solutions for common issues and implementation challenges.</p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start">
                <LightBulbIcon className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Efficient Navigation</h4>
                  <ul className="text-green-800 text-sm space-y-1">
                    <li>• Start with process filtering for targeted searches</li>
                    <li>• Use specific keywords for better search results</li>
                    <li>• Bookmark frequently accessed procedures</li>
                    <li>• Review help ratings to find proven solutions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <UserGroupIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Knowledge Sharing</h4>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>• Contribute solutions to common problems</li>
                    <li>• Rate helpful solutions to improve search results</li>
                    <li>• Document workarounds for system limitations</li>
                    <li>• Share process improvements with the team</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <DocumentTextIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">Content Updates</h4>
                <p className="text-yellow-800 text-sm mb-2">
                  The Knowledge Tree is continuously updated with new processes, improved procedures, and additional help resources.
                  Check regularly for the latest information and best practices.
                </p>
                <div className="bg-white rounded-lg p-3 border border-yellow-300">
                  <p className="text-yellow-800 text-sm">
                    <strong>Pro Tip:</strong> New content is highlighted with update indicators.
                    Subscribe to notifications for important process changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}